import {
  DepositReturnStatus,
  OrderStatus,
  PaymentStatus,
  PayoutClawbackStatus,
  RefundStatus,
  RoyaltyReportStatus,
  SettlementStatus,
  SocialChannelRequestStatus,
  SponsorshipStatus,
} from '@inflip/shared';
import { useEffect, useState } from 'react';

import { api } from './api-client';

/** 처리 화면이 승인·거절 뒤 알리는 이벤트 — 사이드바 빨간 점을 바로 다시 센다 */
export const PENDING_WORK_CHANGED = 'admin:pending-work-changed';

/** 처리 화면에서 부른다 — 사이드바 빨간 점을 바로 다시 센다 */
export function notifyPendingWorkChanged(): void {
  window.dispatchEvent(new Event(PENDING_WORK_CHANGED));
}

const POLL_MS = 60_000;

/** 처리할 일이 남았는지 세는 방법 — 대기 상태 1건만 요청해서 전체 개수(total)를 본다 */
const CHECKS = {
  /** 입금 확인을 기다리는 주문 + 입금 대기 중인 추가금·처리 중인 판매자 위반 신고가 있는 주문 (주문 상태와 별개) */
  orders: (token: string) =>
    Promise.all([
      api.listOrders(token, { status: OrderStatus.PAYMENT_PENDING, pageSize: 1 }),
      api.listOrders(token, { pendingAddon: true, pageSize: 1 }),
      api.listOrders(token, { openViolationReport: true, pageSize: 1 }),
    ]).then((lists) => ({ total: lists.reduce((sum, list) => sum + list.total, 0) })),
  /** 확인을 기다리는 입금 — 주문 대금·추가금 */
  payments: (token: string) =>
    api.listPayments(token, { status: PaymentStatus.PENDING, pageSize: 1 }),
  /** 송금을 기다리는 입금 반환 (초과·부족·만료 후 입금) */
  depositReturns: (token: string) =>
    api.listDepositReturns(token, { status: DepositReturnStatus.PENDING, pageSize: 1 }),
  /** 처리를 기다리는 환불 — 요청됨(승인·거절)·승인됨(송금 뒤 지급 완료) + 기한이 지난 정산 회수 */
  refunds: (token: string) =>
    Promise.all([
      api.listRefunds(token, { status: RefundStatus.REQUESTED, pageSize: 1 }),
      api.listRefunds(token, { status: RefundStatus.APPROVED, pageSize: 1 }),
      api.listRefunds(token, { clawbackStatus: PayoutClawbackStatus.OVERDUE, pageSize: 1 }),
    ]).then((lists) => ({ total: lists.reduce((sum, list) => sum + list.total, 0) })),
  /** 지급 전 정산 — 예정·보류기간·지급 가능·보류(수동) */
  settlements: (token: string) =>
    Promise.all(
      [
        SettlementStatus.SCHEDULED,
        SettlementStatus.HOLDING,
        SettlementStatus.READY,
        SettlementStatus.ON_HOLD,
      ].map((status) => api.listSettlements(token, { status, pageSize: 1 })),
    ).then((lists) => ({ total: lists.reduce((sum, list) => sum + list.total, 0) })),
  /** 심사 대기 리스팅 + 판매 중 리스팅에 새로 추가된 권리(오퍼)의 심사 대기 — 대시보드 집계를 쓴다 */
  listings: (token: string) =>
    api
      .dashboardStats(token)
      .then((stats) => ({ total: stats.reviewPendingCount + stats.offerReviewPendingCount })),
  /** 로열티 — 입금 확인 대기·지급 대기·판매자 이의 */
  royalties: (token: string) =>
    Promise.all([
      ...[RoyaltyReportStatus.REPORTED, RoyaltyReportStatus.CONFIRMED].map((status) =>
        api.listRoyaltyReports(token, { status, pageSize: 1 }),
      ),
      // 판매자 이의가 열린 보고도 처리할 일
      api.listRoyaltyReports(token, { openObjection: true, pageSize: 1 }),
    ]).then((lists) => ({ total: lists.reduce((sum, list) => sum + list.total, 0) })),
  snsRequests: (token: string) =>
    api.listSocialChannelRequests(token, {
      status: SocialChannelRequestStatus.PENDING,
      pageSize: 1,
    }),
  sponsorships: (token: string) =>
    api.listSponsorships(token, { status: SponsorshipStatus.PENDING, pageSize: 1 }),
  /** 검토 대기 신고 — 커뮤니티(대상 단위) + 채팅 */
  reports: (token: string) =>
    api.reportPendingCount(token).then((c) => ({ total: c.community + c.chat })),
  /** 답변을 기다리는 고객센터 1:1 문의 */
  support: (token: string) => api.supportWaitingCount(token).then((c) => ({ total: c.count })),
} satisfies Record<string, (token: string) => Promise<{ total: number }>>;

export type PendingWork = keyof typeof CHECKS;

/**
 * 사이드바 메뉴의 빨간 점 — 입금 대기 주문(추가금 포함)·환불 요청·지급 전 정산·리스팅 심사·로열티 정산 대기·인플루언서 인증 요청·협찬 심사에 대기 중인 건이 있는지.
 * 1분마다, 페이지를 옮길 때마다, 처리 화면에서 승인·거절할 때마다 다시 센다.
 */
export function usePendingWork(
  token: string | undefined,
  pathname: string,
): Record<PendingWork, boolean> {
  const [pending, setPending] = useState<Record<PendingWork, boolean>>({
    orders: false,
    payments: false,
    refunds: false,
    settlements: false,
    depositReturns: false,
    listings: false,
    royalties: false,
    snsRequests: false,
    sponsorships: false,
    reports: false,
    support: false,
  });
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const check = () => {
      for (const key of Object.keys(CHECKS) as PendingWork[]) {
        CHECKS[key](token)
          .then((res) => {
            if (!cancelled) setPending((prev) => ({ ...prev, [key]: res.total > 0 }));
          })
          .catch(() => undefined);
      }
    };
    check();
    const timer = window.setInterval(check, POLL_MS);
    window.addEventListener(PENDING_WORK_CHANGED, check);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener(PENDING_WORK_CHANGED, check);
    };
  }, [token, pathname]);
  return pending;
}
